import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  GripVertical,
  Plus,
  X,
  Loader2,
  Save,
  Search,
  Star,
  Sparkles,
  Power,
  Trash2,
  RotateCcw,
  ListChecks,
  Eye,
  EyeOff,
  ShieldCheck,
  ShieldAlert,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import { ArcheIconImg, DefaultArcheIcon } from '@/lib/arche-icon-url';
import { ArcheGovernanceSwitches } from './ArcheGovernanceSwitches';
import { AppRolesRolloutCard } from './AppRolesRolloutCard';
import api, {
  type ArcheManagementSummary,
  type FileIntakeKind,
  type ArcheLifecycleState,
  type ArcheKindFilter,
  type ArcheGovernanceFilter,
} from '@/lib/api';

// =========================================================================
// Internal vs plugin
// =========================================================================

/**
 * Platform-built arches are everything the sandboxed plugin renderer doesn't
 * draw — the same `render_mode` discriminator the runtime uses. Contributor id
 * would be wrong here: a few platform arches are owned by the system account
 * rather than the Fias platform account.
 */
function isInternalArche(arche: { renderMode: string }): boolean {
  return arche.renderMode !== 'plugin';
}

const KIND_LABELS: Record<ArcheKindFilter, string> = {
  all: 'All arches',
  internal: 'Internal only',
  plugin: 'Plugins only',
};

function KindSelect({
  value,
  onChange,
  className = '',
}: {
  value: ArcheKindFilter;
  onChange: (next: ArcheKindFilter) => void;
  className?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as ArcheKindFilter)}
      aria-label="Filter by arche origin"
      className={`h-9 rounded-md border bg-background px-3 text-sm ${className}`}
    >
      {(Object.keys(KIND_LABELS) as ArcheKindFilter[]).map((kind) => (
        <option key={kind} value={kind}>
          {KIND_LABELS[kind]}
        </option>
      ))}
    </select>
  );
}

function matchesKind(arche: ArcheManagementSummary, kind: ArcheKindFilter): boolean {
  if (kind === 'all') return true;
  return kind === 'internal' ? isInternalArche(arche) : !isInternalArche(arche);
}

/** Case-insensitive match over the two fields an admin actually types. */
function matchesSearch(arche: ArcheManagementSummary, lowerQuery: string): boolean {
  if (!lowerQuery) return true;
  return (
    arche.name.toLowerCase().includes(lowerQuery) || arche.archId.toLowerCase().includes(lowerQuery)
  );
}

// =========================================================================
// Sortable Featured Item
// =========================================================================

/** The minimum a row needs — see `resolveOrPlaceholder` for why it isn't the
 * full summary. */
interface SortableRowArche {
  archId: string;
  name: string;
  imageRef: string | null;
}

/**
 * Featured / suggested lists are stored as bare id arrays, so an id whose arche
 * isn't in the loaded pool used to render nothing at all — an invisible entry
 * that still got re-saved and that nobody could remove. Fall back to showing
 * the raw id instead.
 */
function resolveOrPlaceholder(
  archId: string,
  archeMap: Map<string, ArcheManagementSummary>,
): SortableRowArche {
  return archeMap.get(archId) ?? { archId, name: archId, imageRef: null };
}

function SortableFeaturedItem({
  arche,
  onRemove,
}: {
  arche: SortableRowArche;
  onRemove: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: arche.archId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2 rounded-md border bg-card"
    >
      <button {...attributes} {...listeners} className="cursor-grab p-1 text-muted-foreground">
        <GripVertical className="h-4 w-4" />
      </button>
      {arche.imageRef ? (
        <ArcheIconImg
          imageRef={arche.imageRef}
          size={64}
          alt={arche.name}
          className="w-6 h-6 rounded"
        />
      ) : (
        <DefaultArcheIcon className="w-6 h-6 rounded" />
      )}
      <span className="text-sm flex-1 truncate">{arche.name}</span>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6"
        onClick={() => onRemove(arche.archId)}
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
}

// =========================================================================
// All Arches tab — admin lifecycle (disable / enable / delete / restore)
// =========================================================================

type LifecycleAction =
  | 'disable'
  | 'enable'
  | 'delete'
  | 'restore'
  | 'unlist'
  | 'list'
  // Not lifecycle, but it shares this table's confirm-with-reason machinery.
  // Designating an arche first-party means admin publishes of it skip the paid
  // AI security review — see services/first-party-review-skip.ts.
  | 'designate-first-party'
  | 'undesignate-first-party'
  // The Arche Saves allowlist: this plugin may save into users' Arche Saves
  // automatically (plan §4.5). Also confirm-with-reason; designating also
  // takes the label.
  | 'designate-arche-saves'
  | 'revoke-arche-saves'
  // Plugin file intake (ADR-030): which file kinds this plugin may open from
  // "Open in…". Approving takes the kinds (each must be declared).
  | 'approve-file-intake'
  | 'clear-file-intake';

/** Actions whose audit row is useless without a reason, so the field is required. */
const REASON_REQUIRED_ACTIONS: ReadonlySet<LifecycleAction> = new Set([
  'designate-first-party',
  'undesignate-first-party',
  'designate-arche-saves',
  'revoke-arche-saves',
  'approve-file-intake',
  'clear-file-intake',
]);

interface PendingAction {
  arche: ArcheManagementSummary;
  action: LifecycleAction;
}

function lifecycleBadgeVariant(
  state: ArcheLifecycleState,
): 'default' | 'secondary' | 'destructive' {
  switch (state) {
    case 'active':
      return 'default';
    case 'disabled':
      return 'secondary';
    case 'deleted':
      return 'destructive';
  }
}

/** "document (.docx, .wflow)"-style summary of what an intake opens. */
function fileIntakeSummary(intake: { kinds: FileIntakeKind[]; types: string[] | null }): string {
  return intake.types
    ? `${intake.kinds.join(', ')} (${intake.types.join(', ')})`
    : intake.kinds.join(', ');
}

/**
 * The approve dialog's starting selection. Changing an existing approval
 * starts from what is approved (and still declared) — never from everything
 * declared, which would silently widen it to kinds a republish added.
 */
function initialFileIntakeSelection(arche: ArcheManagementSummary): FileIntakeKind[] {
  const declared = arche.fileIntakeDeclared ?? [];
  if (!arche.fileIntakeKinds) return [...declared];
  const approved = new Set(arche.fileIntakeKinds);
  return declared.filter((k) => approved.has(k));
}

/** Kinds the plugin's manifest declares that an admin has not approved. */
function unapprovedFileIntakeKinds(arche: ArcheManagementSummary): FileIntakeKind[] {
  const approved = new Set(arche.fileIntakeKinds ?? []);
  return (arche.fileIntakeDeclared ?? []).filter((k) => !approved.has(k));
}

function AllArchesTab() {
  const { toast } = useToast();
  const [arches, setArches] = useState<ArcheManagementSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState<'all' | ArcheLifecycleState>('all');
  const [kindFilter, setKindFilter] = useState<ArcheKindFilter>('all');
  // Governance view (ADR-026): the arches a Company owns, or that only chosen
  // people may use — the first thing support asks when an arche "vanished".
  const [governanceFilter, setGovernanceFilter] = useState<ArcheGovernanceFilter>('all');
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [reason, setReason] = useState('');
  const [archeSavesLabel, setArcheSavesLabel] = useState('');
  const [fileIntakeKinds, setFileIntakeKinds] = useState<FileIntakeKind[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // Can THIS deployment honour a review-skip designation? False on prod, where
  // the publish path ignores the flag outright. Defaults false so the action
  // is hidden until the server says otherwise — offering a button that 400s is
  // worse than not offering it.
  const [reviewSkipAvailable, setReviewSkipAvailable] = useState(false);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const result = await api.adminGetArchesSummary({
        limit: 500,
        lifecycle: lifecycleFilter,
        search: search.trim() || undefined,
        kind: kindFilter,
        ...(governanceFilter !== 'all' ? { governance: governanceFilter } : {}),
      });
      const sorted = [...result.arches].sort((a, b) => a.name.localeCompare(b.name));
      setArches(sorted);
      setReviewSkipAvailable(result.reviewSkipAvailable === true);
    } catch (err) {
      toast({ title: 'Failed to load arches', variant: 'destructive' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [lifecycleFilter, kindFilter, governanceFilter, search, toast]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onConfirm = useCallback(async () => {
    if (!pending) return;
    const { arche, action } = pending;
    try {
      setSubmitting(true);
      const trimmed = reason.trim();
      switch (action) {
        case 'disable':
          await api.adminDisableArche(arche.archId, trimmed || undefined);
          toast({ title: `Disabled "${arche.name}"` });
          break;
        case 'enable':
          await api.adminEnableArche(arche.archId);
          toast({ title: `Enabled "${arche.name}"` });
          break;
        case 'delete': {
          const result = await api.adminDeleteArche(arche.archId, trimmed || undefined);
          toast({
            title: result.hardDeleted
              ? `Deleted "${arche.name}" (hard)`
              : `Deleted "${arche.name}" (soft, row preserved)`,
          });
          break;
        }
        case 'restore':
          await api.adminRestoreArche(arche.archId);
          toast({ title: `Restored "${arche.name}" to disabled` });
          break;
        case 'unlist':
          await api.adminUnlistArche(arche.archId, trimmed || undefined);
          toast({ title: `Unlisted "${arche.name}"` });
          break;
        case 'list':
          await api.adminListArche(arche.archId);
          toast({ title: `Listed "${arche.name}"` });
          break;
        case 'designate-first-party':
          await api.adminSetArcheReviewSkip(arche.archId, true, trimmed);
          toast({ title: `"${arche.name}" now skips AI review on admin publishes` });
          break;
        case 'undesignate-first-party':
          await api.adminSetArcheReviewSkip(arche.archId, false, trimmed);
          toast({ title: `"${arche.name}" now runs the full AI review` });
          break;
        case 'designate-arche-saves': {
          const result = await api.adminDesignateArcheSaves(
            arche.archId,
            archeSavesLabel.trim(),
            trimmed,
          );
          toast({ title: `"${arche.name}" may save into Arche Saves/${result.archeSavesLabel}` });
          break;
        }
        case 'revoke-arche-saves':
          await api.adminRevokeArcheSaves(arche.archId, trimmed);
          toast({ title: `"${arche.name}" no longer saves into Arche Saves automatically` });
          break;
        case 'approve-file-intake': {
          const result = await api.adminSetArcheFileIntake(arche.archId, fileIntakeKinds, trimmed);
          toast({ title: `"${arche.name}" may open ${result.fileIntakeKinds.join(', ')} files` });
          break;
        }
        case 'clear-file-intake':
          await api.adminClearArcheFileIntake(arche.archId, trimmed);
          toast({ title: `"${arche.name}" is no longer offered for opening files` });
          break;
      }
      setPending(null);
      setReason('');
      setArcheSavesLabel('');
      setFileIntakeKinds([]);
      await reload();
    } catch (err) {
      toast({
        title: 'Action failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  }, [pending, reason, archeSavesLabel, reload, toast]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">All Arches</CardTitle>
        <CardDescription>
          Search across every arche on the platform. Unlist hides an arche from the store and search
          but keeps it runnable for anyone with the URL; disable temporarily blocks runtime use
          entirely; delete tombstones it (rows are preserved for accounting). Re-enable does NOT
          auto-relist — contributors must explicitly re-list via the publish flow.
        </CardDescription>
        <div className="flex flex-col md:flex-row gap-2 mt-3">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, arche ID, or contributor username / ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
          <select
            value={governanceFilter}
            onChange={(e) => setGovernanceFilter(e.target.value as ArcheGovernanceFilter)}
            aria-label="Filter by governance"
            className="h-9 rounded-md border bg-background px-3 text-sm"
          >
            <option value="all">All ownership and access</option>
            <option value="company-owned">Company-owned only</option>
            <option value="restricted">Restricted only</option>
          </select>
          <select
            value={lifecycleFilter}
            onChange={(e) => setLifecycleFilter(e.target.value as 'all' | ArcheLifecycleState)}
            className="h-9 rounded-md border bg-background px-3 text-sm"
          >
            <option value="all">All lifecycle states</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
            <option value="deleted">Deleted</option>
          </select>
          <KindSelect value={kindFilter} onChange={setKindFilter} />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : arches.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No matching arches.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-2">Name</th>
                  <th className="py-2 pr-2">Origin</th>
                  <th className="py-2 pr-2">Status</th>
                  <th className="py-2 pr-2">Lifecycle</th>
                  <th className="py-2 pr-2">Listed</th>
                  <th className="py-2 pr-2">Contributor</th>
                  <th className="py-2 pl-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {arches.map((arche) => (
                  <tr key={arche.archId} className="border-b/50">
                    <td className="py-2 pr-2">
                      <div className="flex items-center gap-2">
                        {arche.imageRef ? (
                          <ArcheIconImg
                            imageRef={arche.imageRef}
                            size={64}
                            alt={arche.name}
                            className="w-6 h-6 rounded"
                          />
                        ) : (
                          <DefaultArcheIcon className="w-6 h-6 rounded" />
                        )}
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate font-medium">{arche.name}</span>
                            {arche.skipAiReview && (
                              <Badge
                                variant="secondary"
                                className="text-[10px] gap-0.5 shrink-0"
                                title={
                                  `Admin publishes skip the AI security review.` +
                                  (arche.skipAiReviewSetBy
                                    ? ` Set by ${arche.skipAiReviewSetBy}`
                                    : '') +
                                  (arche.skipAiReviewSetAt
                                    ? ` on ${new Date(arche.skipAiReviewSetAt).toLocaleDateString()}`
                                    : '')
                                }
                              >
                                <ShieldAlert className="h-2.5 w-2.5" /> first-party
                              </Badge>
                            )}
                            {arche.archeSavesLabel && (
                              <Badge
                                variant="secondary"
                                className="text-[10px] shrink-0"
                                title={`Saves into users' Arche Saves/${arche.archeSavesLabel} automatically`}
                              >
                                Arche Saves: {arche.archeSavesLabel}
                              </Badge>
                            )}
                            {(arche.fileIntakeEffective?.kinds.length ?? 0) > 0 && (
                              <Badge
                                variant="secondary"
                                className="text-[10px] shrink-0"
                                title="Offered in My Data's “Open in…” for these files now (approved ∩ declared)"
                              >
                                Opens: {fileIntakeSummary(arche.fileIntakeEffective!)}
                              </Badge>
                            )}
                            {unapprovedFileIntakeKinds(arche).length > 0 && (
                              <Badge
                                variant="outline"
                                className="text-[10px] shrink-0"
                                title="Its manifest declares fileIntake kinds an admin has not approved"
                              >
                                Wants to open: {unapprovedFileIntakeKinds(arche).join(', ')}
                              </Badge>
                            )}
                            {arche.owningCompanyId && (
                              <Badge
                                variant="secondary"
                                className="text-[10px] shrink-0"
                                title={`Owned by a Company (${arche.owningCompanyId}); its owners and admins govern it, its payer is paid`}
                              >
                                Company: {arche.owningCompanyName ?? arche.owningCompanyId}
                              </Badge>
                            )}
                            {arche.isRestricted && (
                              <Badge
                                variant="outline"
                                className="text-[10px] shrink-0"
                                title="Only its team and chosen people, workspaces, Companies or guilds may use it; never in the Store or search"
                              >
                                restricted
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground truncate font-mono">
                            {arche.archId}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 pr-2">
                      {isInternalArche(arche) ? (
                        <Badge variant="secondary" className="text-xs" title={arche.renderMode}>
                          internal
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">
                          plugin
                        </Badge>
                      )}
                    </td>
                    <td className="py-2 pr-2">
                      <Badge variant="outline" className="text-xs">
                        {arche.status}
                      </Badge>
                    </td>
                    <td className="py-2 pr-2">
                      <Badge
                        variant={lifecycleBadgeVariant(arche.lifecycleState)}
                        className="text-xs"
                      >
                        {arche.lifecycleState}
                      </Badge>
                    </td>
                    <td className="py-2 pr-2 text-xs">
                      {arche.isListed ? 'yes' : <span className="text-muted-foreground">no</span>}
                    </td>
                    <td className="py-2 pr-2 text-xs truncate max-w-[160px]">
                      {arche.contributorUsername ? (
                        <span title={arche.contributorId ?? undefined}>
                          {arche.contributorUsername}
                        </span>
                      ) : arche.contributorId ? (
                        <span
                          className="font-mono text-muted-foreground"
                          title={arche.contributorId}
                        >
                          {arche.contributorId}
                        </span>
                      ) : (
                        <span className="text-muted-foreground" title="No contributor">
                          —
                        </span>
                      )}
                    </td>
                    <td className="py-2 pl-2 text-right space-x-1">
                      {arche.lifecycleState === 'active' && arche.isListed && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPending({ arche, action: 'unlist' })}
                          className="gap-1"
                        >
                          <EyeOff className="h-3 w-3" /> Unlist
                        </Button>
                      )}
                      {/* A restricted arche is never listed (ADR-026 CHECK); the
                          server would refuse with ARCHE_RESTRICTED. */}
                      {arche.lifecycleState === 'active' &&
                        !arche.isListed &&
                        !arche.isRestricted && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPending({ arche, action: 'list' })}
                            className="gap-1"
                          >
                            <Eye className="h-3 w-3" /> List
                          </Button>
                        )}
                      {arche.lifecycleState === 'active' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPending({ arche, action: 'disable' })}
                          className="gap-1"
                        >
                          <Power className="h-3 w-3" /> Disable
                        </Button>
                      )}
                      {arche.lifecycleState === 'disabled' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPending({ arche, action: 'enable' })}
                          className="gap-1"
                        >
                          <Power className="h-3 w-3" /> Enable
                        </Button>
                      )}
                      {arche.lifecycleState === 'deleted' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPending({ arche, action: 'restore' })}
                          className="gap-1"
                        >
                          <RotateCcw className="h-3 w-3" /> Restore
                        </Button>
                      )}
                      {/* Only plugin arches publish through the review pipeline,
                          so the designation is meaningless on an internal one.

                          The two halves are gated differently on purpose.
                          "Require review" (clearing) shows wherever a
                          designation exists — turning a permission off must
                          never be the thing that is unavailable, and prod may
                          still carry rows predating the staging-only rule.
                          "Skip review" (setting) shows only where the deployment
                          can actually honour it, because on prod the route
                          refuses it and the publish path ignores it. */}
                      {!isInternalArche(arche) &&
                        arche.lifecycleState !== 'deleted' &&
                        (arche.skipAiReview ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPending({ arche, action: 'undesignate-first-party' })}
                            className="gap-1"
                          >
                            <ShieldCheck className="h-3 w-3" /> Require review
                          </Button>
                        ) : (
                          reviewSkipAvailable && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setPending({ arche, action: 'designate-first-party' })}
                              className="gap-1"
                            >
                              <ShieldAlert className="h-3 w-3" /> Skip review
                            </Button>
                          )
                        ))}
                      {/* The Arche Saves allowlist — plugins only; internal
                          arches save there from code. Revoking is always
                          offered where a designation exists. */}
                      {!isInternalArche(arche) &&
                        (arche.archeSavesLabel ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPending({ arche, action: 'revoke-arche-saves' })}
                          >
                            Stop Arche Saves
                          </Button>
                        ) : (
                          arche.lifecycleState !== 'deleted' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setPending({ arche, action: 'designate-arche-saves' })}
                            >
                              Allow Arche Saves
                            </Button>
                          )
                        ))}
                      {/* Plugin file intake (ADR-030) — plugins whose manifest
                          declares kinds. Withdrawing is always offered where an
                          approval exists. */}
                      {!isInternalArche(arche) &&
                        arche.lifecycleState !== 'deleted' &&
                        (arche.fileIntakeDeclared?.length ?? 0) > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setFileIntakeKinds(initialFileIntakeSelection(arche));
                              setPending({ arche, action: 'approve-file-intake' });
                            }}
                          >
                            {arche.fileIntakeKinds ? 'Change file opening' : 'Allow file opening'}
                          </Button>
                        )}
                      {!isInternalArche(arche) && arche.fileIntakeKinds && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPending({ arche, action: 'clear-file-intake' })}
                        >
                          Stop file opening
                        </Button>
                      )}
                      {arche.lifecycleState !== 'deleted' && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setPending({ arche, action: 'delete' })}
                          className="gap-1"
                        >
                          <Trash2 className="h-3 w-3" /> Delete
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Confirmation modal */}
      {pending && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !submitting && setPending(null)}
        >
          <div
            className="bg-background border rounded-lg shadow-lg max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold">
              {pending.action === 'disable' && 'Disable arche?'}
              {pending.action === 'enable' && 'Enable arche?'}
              {pending.action === 'delete' && 'Delete arche?'}
              {pending.action === 'restore' && 'Restore arche?'}
              {pending.action === 'unlist' && 'Unlist arche?'}
              {pending.action === 'list' && 'List arche?'}
              {pending.action === 'designate-first-party' && 'Skip AI review for this arche?'}
              {pending.action === 'undesignate-first-party' && 'Require AI review again?'}
              {pending.action === 'designate-arche-saves' && 'Allow automatic Arche Saves?'}
              {pending.action === 'revoke-arche-saves' && 'Stop automatic Arche Saves?'}
              {pending.action === 'approve-file-intake' && 'Allow this app to open files?'}
              {pending.action === 'clear-file-intake' && 'Stop offering this app for files?'}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{pending.arche.name}</span>
              {' — '}
              {pending.action === 'disable' &&
                'temporarily removes this arche from the store and blocks runtime use. Reversible.'}
              {pending.action === 'enable' &&
                'returns the arche to active. Does NOT re-list it; contributor must explicitly relist.'}
              {pending.action === 'delete' &&
                (pending.arche.status === 'draft'
                  ? 'permanently hard-deletes this draft.'
                  : 'tombstones the arche. The row stays so creator earnings, sessions, and entitlements still join, but every public surface treats it as gone.')}
              {pending.action === 'restore' &&
                'returns the arche to disabled (not active). The contributor must re-enable + relist to bring it back to the store.'}
              {pending.action === 'unlist' &&
                'hides this arche from the store, search, and featured lists. Anyone with the URL can still open and use it. Reversible.'}
              {pending.action === 'list' &&
                'puts this arche back in the store and search. Fails if another contributor has taken the name during the unlist window.'}
              {pending.action === 'designate-first-party' &&
                'publishes by a platform ADMIN will skip the paid AI security analysis and will not be gated by its findings. Static analysis still runs and is still recorded. A non-admin publishing this arche is unaffected and still gets the full review. Reversible at any time.'}
              {pending.action === 'undesignate-first-party' &&
                'every publish of this arche runs the full AI security review again, and the publisher is charged for it.'}
              {pending.action === 'designate-arche-saves' &&
                "may save files into every user's Arche Saves/<label> folder without asking each time — once it also publishes the vault:arche-saves:write permission (which forces human review). The label is this arche's forever: it can be changed or revoked, never given to another arche."}
              {pending.action === 'revoke-arche-saves' &&
                'stops new automatic saves immediately. Files already saved stay with their users, and the label stays reserved to this arche.'}
              {pending.action === 'approve-file-intake' &&
                "will be offered in every user's My Data “Open in…” for the kinds below, and becomes the double-click default for documents, spreadsheets and presentations. Opening shares that one file with the app, read-only. A kind its manifest adds later stays off until approved here."}
              {pending.action === 'clear-file-intake' &&
                'stops offering this app for opening files immediately. Grants users already gave it for specific files are unaffected.'}
            </p>
            {pending.action === 'approve-file-intake' && (
              <fieldset className="mt-4">
                <legend className="text-sm font-medium">
                  File kinds (declared by its manifest)
                </legend>
                <div className="mt-1 flex flex-wrap gap-3">
                  {(pending.arche.fileIntakeDeclared ?? []).map((kind) => (
                    <label key={kind} className="flex items-center gap-1.5 text-sm">
                      <input
                        type="checkbox"
                        checked={fileIntakeKinds.includes(kind)}
                        onChange={(e) =>
                          setFileIntakeKinds((prev) =>
                            e.target.checked ? [...prev, kind] : prev.filter((k) => k !== kind),
                          )
                        }
                      />
                      {kind}
                    </label>
                  ))}
                </div>
                {pending.arche.fileIntakeDeclaredTypes && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Limited to: {pending.arche.fileIntakeDeclaredTypes.join(', ')} — approved
                    together with the kinds. Types added in a later publish stay off until approved
                    again.
                  </p>
                )}
              </fieldset>
            )}
            {pending.action === 'designate-arche-saves' && (
              <div className="mt-4">
                <label htmlFor="arche-saves-label" className="text-sm font-medium">
                  Folder label (required)
                </label>
                <Input
                  id="arche-saves-label"
                  value={archeSavesLabel}
                  onChange={(e) => setArcheSavesLabel(e.target.value)}
                  placeholder="e.g. Sketch Pad"
                  className="mt-1"
                  maxLength={100}
                />
              </div>
            )}
            {pending.action === 'designate-first-party' && (
              <p className="mt-2 text-xs text-muted-foreground">
                Only do this for arches the platform owns. An admin API key that leaks could publish
                unreviewed code to any arche carrying this designation.
              </p>
            )}
            {(pending.action === 'disable' ||
              pending.action === 'delete' ||
              pending.action === 'unlist' ||
              REASON_REQUIRED_ACTIONS.has(pending.action)) && (
              <div className="mt-4">
                <label htmlFor="reason" className="text-sm font-medium">
                  {REASON_REQUIRED_ACTIONS.has(pending.action)
                    ? 'Reason (required)'
                    : 'Reason (optional)'}
                </label>
                <Input
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Recorded in the audit log"
                  className="mt-1"
                  maxLength={500}
                />
              </div>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setPending(null);
                  setReason('');
                  setArcheSavesLabel('');
                  setFileIntakeKinds([]);
                }}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant={pending.action === 'delete' ? 'destructive' : 'default'}
                onClick={onConfirm}
                disabled={
                  submitting ||
                  (REASON_REQUIRED_ACTIONS.has(pending.action) && reason.trim().length === 0) ||
                  (pending.action === 'designate-arche-saves' &&
                    archeSavesLabel.trim().length === 0) ||
                  (pending.action === 'approve-file-intake' && fileIntakeKinds.length === 0)
                }
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

// =========================================================================
// Main Component
// =========================================================================

export function ArcheManagement() {
  const { toast } = useToast();

  // All arches for pickers
  const [allArches, setAllArches] = useState<ArcheManagementSummary[]>([]);
  const [loading, setLoading] = useState(true);

  // Featured arches state
  const [featuredIds, setFeaturedIds] = useState<string[]>([]);
  const [featuredDirty, setFeaturedDirty] = useState(false);
  const [featuredSaving, setFeaturedSaving] = useState(false);
  const [featuredSearch, setFeaturedSearch] = useState('');

  // Suggested arches state
  const [selectedArcheId, setSelectedArcheId] = useState<string | null>(null);
  const [suggestedIds, setSuggestedIds] = useState<string[]>([]);
  const [suggestedDirty, setSuggestedDirty] = useState(false);
  const [suggestedSaving, setSuggestedSaving] = useState(false);
  const [suggestedSearch, setSuggestedSearch] = useState('');
  const [suggestedKind, setSuggestedKind] = useState<ArcheKindFilter>('all');

  // Which arche's suggestions are being edited — the left-hand picker.
  const [archeListSearch, setArcheListSearch] = useState('');
  const [archeListKind, setArcheListKind] = useState<ArcheKindFilter>('all');

  // Fetch all arches + featured list on mount
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Two windows, merged. The plain listing is `created_at DESC LIMIT 500`,
      // so once enough developer plugins accumulate the platform's own arches —
      // the OLDEST rows in the table — fall off the end and disappear from every
      // picker on this page. The internal set is small and bounded, so asking
      // for it explicitly keeps it present no matter how many plugins exist.
      const [internalResult, recentResult, featuredResult] = await Promise.all([
        api.adminGetArchesSummary({ limit: 500, kind: 'internal' }),
        api.adminGetArchesSummary({ limit: 500 }),
        api.adminGetFeaturedArches(),
      ]);
      const byId = new Map<string, ArcheManagementSummary>();
      for (const arche of [...internalResult.arches, ...recentResult.arches]) {
        byId.set(arche.archId, arche);
      }
      setAllArches([...byId.values()].sort((a, b) => a.name.localeCompare(b.name)));
      setFeaturedIds(featuredResult.featured.map((f) => f.archId));
    } catch (err) {
      toast({ title: 'Failed to load data', variant: 'destructive' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Arche lookup map
  const archeMap = useMemo(() => {
    const map = new Map<string, ArcheManagementSummary>();
    for (const a of allArches) map.set(a.archId, a);
    return map;
  }, [allArches]);

  // =========================================================================
  // Featured Arches handlers
  // =========================================================================

  const featuredArches = useMemo(
    () => featuredIds.map((id) => resolveOrPlaceholder(id, archeMap)),
    [featuredIds, archeMap],
  );

  const featuredSet = useMemo(() => new Set(featuredIds), [featuredIds]);

  const publishedArches = useMemo(
    () => allArches.filter((a) => a.status === 'published'),
    [allArches],
  );

  const filteredAvailableArches = useMemo(() => {
    const q = featuredSearch.toLowerCase();
    return publishedArches.filter(
      (a) =>
        !featuredSet.has(a.archId) &&
        (a.name.toLowerCase().includes(q) || a.archId.toLowerCase().includes(q)),
    );
  }, [publishedArches, featuredSet, featuredSearch]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleFeaturedDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setFeaturedIds((prev) => {
      const oldIndex = prev.indexOf(String(active.id));
      const newIndex = prev.indexOf(String(over.id));
      return arrayMove(prev, oldIndex, newIndex);
    });
    setFeaturedDirty(true);
  }, []);

  const addFeatured = useCallback((archId: string) => {
    setFeaturedIds((prev) => [...prev, archId]);
    setFeaturedDirty(true);
  }, []);

  const removeFeatured = useCallback((archId: string) => {
    setFeaturedIds((prev) => prev.filter((id) => id !== archId));
    setFeaturedDirty(true);
  }, []);

  const saveFeatured = useCallback(async () => {
    try {
      setFeaturedSaving(true);
      const result = await api.adminSetFeaturedArches(featuredIds);
      setFeaturedIds(result.featured.map((f) => f.archId));
      setFeaturedDirty(false);
      toast({ title: 'Featured arches saved' });
    } catch (err) {
      toast({ title: 'Failed to save', variant: 'destructive' });
      console.error(err);
    } finally {
      setFeaturedSaving(false);
    }
  }, [featuredIds, toast]);

  // =========================================================================
  // Suggested Arches handlers
  // =========================================================================

  const loadSuggested = useCallback(async (archId: string) => {
    setSelectedArcheId(archId);
    setSuggestedDirty(false);
    setSuggestedSearch('');
    try {
      const result = await api.adminGetSuggestedArches(archId);
      setSuggestedIds(result.suggestedArches);
    } catch {
      setSuggestedIds([]);
    }
  }, []);

  const suggestedArches = useMemo(
    () => suggestedIds.map((id) => resolveOrPlaceholder(id, archeMap)),
    [suggestedIds, archeMap],
  );

  const suggestedSet = useMemo(() => new Set(suggestedIds), [suggestedIds]);

  const filteredSuggestableArches = useMemo(() => {
    const q = suggestedSearch.trim().toLowerCase();
    return allArches.filter(
      (a) =>
        a.archId !== selectedArcheId &&
        !suggestedSet.has(a.archId) &&
        matchesKind(a, suggestedKind) &&
        matchesSearch(a, q),
    );
  }, [allArches, selectedArcheId, suggestedSet, suggestedSearch, suggestedKind]);

  /** The left-hand "which arche am I editing" list. */
  const selectableArches = useMemo(() => {
    const q = archeListSearch.trim().toLowerCase();
    return allArches.filter((a) => matchesKind(a, archeListKind) && matchesSearch(a, q));
  }, [allArches, archeListKind, archeListSearch]);

  const handleSuggestedDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSuggestedIds((prev) => {
      const oldIndex = prev.indexOf(String(active.id));
      const newIndex = prev.indexOf(String(over.id));
      return arrayMove(prev, oldIndex, newIndex);
    });
    setSuggestedDirty(true);
  }, []);

  const addSuggested = useCallback((archId: string) => {
    setSuggestedIds((prev) => [...prev, archId]);
    setSuggestedDirty(true);
  }, []);

  const removeSuggested = useCallback((archId: string) => {
    setSuggestedIds((prev) => prev.filter((id) => id !== archId));
    setSuggestedDirty(true);
  }, []);

  const saveSuggested = useCallback(async () => {
    if (!selectedArcheId) return;
    try {
      setSuggestedSaving(true);
      const result = await api.adminSetSuggestedArches(selectedArcheId, suggestedIds);
      setSuggestedIds(result.suggestedArches);
      setSuggestedDirty(false);
      toast({ title: 'Suggested arches saved' });
    } catch (err) {
      toast({ title: 'Failed to save', variant: 'destructive' });
      console.error(err);
    } finally {
      setSuggestedSaving(false);
    }
  }, [selectedArcheId, suggestedIds, toast]);

  // =========================================================================
  // Render
  // =========================================================================

  if (loading) {
    return (
      <div className="w-full max-w-6xl mx-auto p-4 sm:p-8">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto p-8">
      <div className="mb-6">
        <Link
          to="/dev-console"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Dev Console
        </Link>
        <h1 className="text-3xl font-bold">Arche Management</h1>
        <p className="mt-1 text-muted-foreground">
          Manage featured arches on the marketplace and per-arche suggested arches in the sidebar.
        </p>
      </div>

      <Tabs defaultValue="featured">
        {/* Wrap tabs onto multiple rows on a narrow screen rather than
            overflowing the page. */}
        <TabsList className="flex h-auto flex-wrap justify-start gap-1">
          <TabsTrigger value="featured" className="gap-1.5">
            <Star className="h-4 w-4" />
            Featured Arches
          </TabsTrigger>
          <TabsTrigger value="suggested" className="gap-1.5">
            <Sparkles className="h-4 w-4" />
            Suggested Arches
          </TabsTrigger>
          <TabsTrigger value="all" className="gap-1.5">
            <ListChecks className="h-4 w-4" />
            All Arches
          </TabsTrigger>
        </TabsList>

        {/* ============================================================ */}
        {/* Featured Arches Tab */}
        {/* ============================================================ */}
        <TabsContent value="featured" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Available arches */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Available Arches</CardTitle>
                <CardDescription>Click + to add an arche to the featured list</CardDescription>
                <div className="relative mt-2">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search arches..."
                    value={featuredSearch}
                    onChange={(e) => setFeaturedSearch(e.target.value)}
                    className="pl-9 h-9"
                  />
                </div>
              </CardHeader>
              <CardContent className="max-h-[500px] overflow-y-auto space-y-1">
                {filteredAvailableArches.map((arche) => (
                  <div
                    key={arche.archId}
                    className="flex items-center gap-2 p-2 rounded-md hover:bg-muted"
                  >
                    {arche.imageRef ? (
                      <ArcheIconImg
                        imageRef={arche.imageRef}
                        size={64}
                        alt={arche.name}
                        className="w-6 h-6 rounded"
                      />
                    ) : (
                      <DefaultArcheIcon className="w-6 h-6 rounded" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{arche.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {arche.archeType} · {arche.archId}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 flex-shrink-0"
                      onClick={() => addFeatured(arche.archId)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {filteredAvailableArches.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No matching arches
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Right: Current featured list (drag-to-reorder) */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-base">
                      Featured List ({featuredIds.length})
                    </CardTitle>
                    <CardDescription>Drag to reorder. Top arche appears first.</CardDescription>
                  </div>
                  <Button
                    size="sm"
                    onClick={saveFeatured}
                    disabled={!featuredDirty || featuredSaving}
                    className="gap-1.5"
                  >
                    {featuredSaving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    Save
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="max-h-[500px] overflow-y-auto space-y-1">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleFeaturedDragEnd}
                >
                  <SortableContext items={featuredIds} strategy={verticalListSortingStrategy}>
                    {featuredArches.map((arche) => (
                      <SortableFeaturedItem
                        key={arche.archId}
                        arche={arche}
                        onRemove={removeFeatured}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
                {featuredIds.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No featured arches. Add some from the left panel.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============================================================ */}
        {/* Suggested Arches Tab */}
        {/* ============================================================ */}
        <TabsContent value="suggested" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Arche selector */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Arches ({selectableArches.length})</CardTitle>
                <CardDescription>Select an arche to edit its suggestions</CardDescription>
                <div className="mt-2 space-y-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or arche ID..."
                      value={archeListSearch}
                      onChange={(e) => setArcheListSearch(e.target.value)}
                      className="pl-9 h-9"
                    />
                  </div>
                  <KindSelect
                    value={archeListKind}
                    onChange={setArcheListKind}
                    className="w-full"
                  />
                </div>
              </CardHeader>
              <CardContent className="max-h-[500px] overflow-y-auto space-y-0.5">
                {selectableArches.map((arche) => (
                  <button
                    key={arche.archId}
                    onClick={() => loadSuggested(arche.archId)}
                    className={`w-full flex items-center gap-2 p-2 rounded-md text-left transition-colors ${
                      selectedArcheId === arche.archId
                        ? 'bg-primary/10 text-primary'
                        : 'hover:bg-muted'
                    }`}
                  >
                    {arche.imageRef ? (
                      <ArcheIconImg
                        imageRef={arche.imageRef}
                        size={64}
                        alt={arche.name}
                        className="w-6 h-6 rounded"
                      />
                    ) : (
                      <DefaultArcheIcon className="w-6 h-6 rounded" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{arche.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{arche.archId}</div>
                    </div>
                    {(arche.suggestedArches?.length ?? 0) > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {arche.suggestedArches.length}
                      </Badge>
                    )}
                  </button>
                ))}
                {selectableArches.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No matching arches
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Right: Suggested arches editor (spans 2 cols) */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">
                      {selectedArcheId
                        ? `Suggestions for ${archeMap.get(selectedArcheId)?.name ?? selectedArcheId}`
                        : 'Select an arche'}
                    </CardTitle>
                    <CardDescription>
                      These arches appear in the sidebar when a user is viewing the selected arche.
                    </CardDescription>
                  </div>
                  {selectedArcheId && (
                    <Button
                      size="sm"
                      onClick={saveSuggested}
                      disabled={!suggestedDirty || suggestedSaving}
                      className="gap-1.5"
                    >
                      {suggestedSaving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      Save
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {!selectedArcheId ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Select an arche from the left panel to manage its suggestions.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {/* Current suggestions (drag to reorder) */}
                    <div>
                      <p className="text-sm font-medium mb-2">
                        Current suggestions ({suggestedIds.length})
                      </p>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleSuggestedDragEnd}
                      >
                        <SortableContext
                          items={suggestedIds}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-1">
                            {suggestedArches.map((arche) => (
                              <SortableFeaturedItem
                                key={arche.archId}
                                arche={arche}
                                onRemove={removeSuggested}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                      {suggestedIds.length === 0 && (
                        <p className="text-sm text-muted-foreground py-2">
                          No suggestions configured
                        </p>
                      )}
                    </div>

                    {/* Add suggestions */}
                    <div>
                      <p className="text-sm font-medium mb-2">Add suggestion</p>
                      <div className="mb-2 flex flex-col gap-2 sm:flex-row">
                        <div className="relative flex-1">
                          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Search arches to add..."
                            value={suggestedSearch}
                            onChange={(e) => setSuggestedSearch(e.target.value)}
                            className="pl-9 h-9"
                          />
                        </div>
                        <KindSelect value={suggestedKind} onChange={setSuggestedKind} />
                      </div>
                      <div className="max-h-[300px] overflow-y-auto space-y-1 border rounded-md p-1">
                        {filteredSuggestableArches.slice(0, 50).map((arche) => (
                          <div
                            key={arche.archId}
                            className="flex items-center gap-2 p-2 rounded hover:bg-muted"
                          >
                            {arche.imageRef ? (
                              <ArcheIconImg
                                imageRef={arche.imageRef}
                                size={64}
                                alt={arche.name}
                                className="w-5 h-5 rounded"
                              />
                            ) : (
                              <DefaultArcheIcon className="w-5 h-5 rounded" />
                            )}
                            <span className="text-sm flex-1 truncate">{arche.name}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => addSuggested(arche.archId)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                        {filteredSuggestableArches.length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-3">
                            No matching arches
                          </p>
                        )}
                        {/* The list is capped at 50 — say so, rather than
                            letting it look like the search found everything. */}
                        {filteredSuggestableArches.length > 50 && (
                          <p className="text-xs text-muted-foreground text-center py-2">
                            Showing 50 of {filteredSuggestableArches.length} — narrow the search or
                            filter by origin.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ============================================================ */}
        {/* All Arches Tab — admin lifecycle controls                     */}
        {/* ============================================================ */}
        <TabsContent value="all" className="mt-4 space-y-4">
          <ArcheGovernanceSwitches />
          <AppRolesRolloutCard />
          <AllArchesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default ArcheManagement;
